import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Loader2, Lock, AlertCircle, X } from 'lucide-react';
import { paymentsAPI } from '../../services/api';

/**
 * The card form, for whichever gateway this portal takes payments through.
 *
 * Two gateways collect a card in two fundamentally different places:
 *
 *   - Authorize.net takes the card number on the server, so the portal's own
 *     inputs post it (they are passed in as `children` and rendered as they
 *     always were — nothing about that path changed).
 *   - The hosted gateway never lets a card number touch the portal. The
 *     server begins a SetupIntent (saving a method) or a PaymentIntent (a
 *     one-off payment) and hands back a client secret; the gateway's own
 *     PaymentElement collects and confirms the card in the browser; the
 *     portal then posts only the resulting id, which the server verifies.
 *
 * Customer-facing text here names no vendor: an MSP rebrands this portal,
 * and its clients see "secure card form", not a processor's name.
 */

/**
 * One Stripe.js instance per publishable key and connected account, for the
 * life of the page. Platform payments are direct charges on the MSP's
 * connected account, so Stripe.js must be initialised with `{ stripeAccount }`
 * or it cannot see the intents the server created there.
 */
const stripeByKey = new Map();
export function stripeFor(publishableKey, stripeAccount = null) {
  if (!publishableKey) return null;
  const key = `${publishableKey}\u0000${stripeAccount || ''}`;
  if (!stripeByKey.has(key)) stripeByKey.set(key, stripeAccount ? loadStripe(publishableKey, { stripeAccount }) : loadStripe(publishableKey));
  return stripeByKey.get(key);
}

/**
 * The gateway for the signed-in user's portal: { gateway, publishableKey,
 * capabilities }. Defaults to Authorize.net while loading or on error, which
 * is the historical behaviour and the safe one: the raw form posts to an
 * endpoint that refuses card numbers when the gateway does not take them.
 */
export function usePaymentGateway({ enabled = true } = {}) {
  const { data, isLoading } = useQuery({
    queryKey: ['payment-gateway'],
    queryFn: () => paymentsAPI.getGateway().then((r) => r.data),
    enabled,
    retry: false,
  });
  return {
    gateway: data?.gateway || 'authorizenet',
    publishableKey: data?.publishableKey || null,
    stripeAccount: data?.stripeAccount || null,
    configured: data?.configured !== false,
    isLoading: enabled && isLoading,
  };
}

/** Both the MSP's own Stripe and platform payments take the card in the hosted form. */
const HOSTED = new Set(['stripe', 'stripe_connect']);
export const isHostedGateway = (g) => HOSTED.has(typeof g === 'string' ? g : g?.gateway);

const buttonClass =
  'inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50';

/** The PaymentElement and its confirm button. Inside <Elements>. */
function ConfirmInner({ mode, submitLabel, onConfirmed, onError, disabled, returnUrl }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [ready, setReady] = useState(false);

  const confirm = async () => {
    if (!stripe || !elements) return;
    setBusy(true);
    setError('');
    try {
      const submitted = await elements.submit();
      if (submitted.error) throw submitted.error;
      const confirmParams = { return_url: returnUrl || window.location.href };
      if (mode === 'setup') {
        const { error: err, setupIntent } = await stripe.confirmSetup({ elements, confirmParams, redirect: 'if_required' });
        if (err) throw err;
        if (setupIntent.status === 'requires_action' && setupIntent.next_action?.type === 'verify_with_microdeposits') {
          throw new Error('This bank account needs to be verified with small test deposits, which take one to two business days. Use instant verification by signing in to your bank, or add a card instead.');
        }
        if (setupIntent.status !== 'succeeded' && setupIntent.status !== 'processing') throw new Error('The payment method could not be confirmed. Please try again.');
        await onConfirmed({ paymentMethodId: setupIntent.payment_method, setupIntentId: setupIntent.id });
      } else {
        const { error: err, paymentIntent } = await stripe.confirmPayment({ elements, confirmParams, redirect: 'if_required' });
        if (err) throw err;
        if (paymentIntent.status !== 'succeeded' && paymentIntent.status !== 'processing') throw new Error('The payment was not completed. Please try again.');
        await onConfirmed({ paymentIntentId: paymentIntent.id });
      }
    } catch (err) {
      const message = err?.response?.data?.error || err?.message || 'Something went wrong. Please try again.';
      setError(message);
      onError?.(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <PaymentElement onReady={() => setReady(true)} options={{ layout: 'tabs' }} />
      {!ready && (
        <div className="flex items-center gap-2 text-sm text-neutral-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading secure card form…</div>
      )}
      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" /> {error}
        </div>
      )}
      <button type="button" onClick={confirm} disabled={!stripe || !ready || busy || disabled} className={buttonClass}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
        {submitLabel}
      </button>
      <p className="flex items-center justify-center gap-1 text-xs text-neutral-500 dark:text-neutral-400">
        <Lock className="h-3 w-3" /> Your card details go straight to the payment processor and never touch this portal.
      </p>
    </div>
  );
}

/**
 * Hosted card form: begins an intent with `begin()` (which must resolve to
 * `{ clientSecret }`), then renders the PaymentElement against it.
 * `begin` is called once per mount; remount (change `key`) to start over.
 */
export function HostedCardForm({ publishableKey, stripeAccount = null, mode = 'setup', begin, onConfirmed, onError, submitLabel = 'Save', disabled = false }) {
  const [clientSecret, setClientSecret] = useState(null);
  const [error, setError] = useState('');
  const started = useRef(false);
  const stripePromise = useMemo(() => stripeFor(publishableKey, stripeAccount), [publishableKey, stripeAccount]);
  const dark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    Promise.resolve(begin())
      .then((res) => {
        const secret = res?.data?.clientSecret ?? res?.clientSecret;
        if (!secret) throw new Error('The payment form could not be started.');
        setClientSecret(secret);
      })
      .catch((err) => setError(err?.response?.data?.error || err?.message || 'The payment form could not be started.'));
  }, [begin]);

  if (!publishableKey) {
    return <p className="text-sm text-red-600">Online card payments are not set up for this portal yet. Please contact support.</p>;
  }
  if (error) {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
        <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" /> {error}
      </div>
    );
  }
  if (!clientSecret) {
    return <div className="flex items-center gap-2 text-sm text-neutral-500"><Loader2 className="h-4 w-4 animate-spin" /> Preparing secure card form…</div>;
  }
  return (
    <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: dark ? 'night' : 'stripe' } }}>
      <ConfirmInner mode={mode} submitLabel={submitLabel} onConfirmed={onConfirmed} onError={onError} disabled={disabled} />
    </Elements>
  );
}

/**
 * The gateway-aware form. Authorize.net: renders `children` (the portal's
 * own inputs, which post the card). Hosted gateway: the PaymentElement.
 */
export default function CardForm({ gateway, publishableKey, stripeAccount = null, children, ...hosted }) {
  if (!isHostedGateway(gateway)) return children;
  return <HostedCardForm publishableKey={publishableKey} stripeAccount={stripeAccount} {...hosted} />;
}

/**
 * Saving a card or bank account on the hosted gateway, as a whole modal or
 * inline. `begin()` starts the SetupIntent; `save({ paymentMethodId, isDefault,
 * friendlyName })` posts the confirmed id to the portal.
 */
export function HostedAddMethod({ methodType = 'card', publishableKey, stripeAccount = null, begin, save, onSuccess, onCancel, inline = false, title }) {
  const [isDefault, setIsDefault] = useState(false);
  const [friendlyName, setFriendlyName] = useState('');
  const label = methodType === 'ach' ? 'bank account' : 'card';

  const body = (
    <div className="space-y-4">
      <div>
        <label className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300">Nickname (optional)</label>
        <input
          type="text"
          value={friendlyName}
          onChange={(e) => setFriendlyName(e.target.value)}
          placeholder={methodType === 'ach' ? 'e.g. Operating account' : 'e.g. Company card'}
          className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm dark:border-neutral-600 dark:bg-neutral-900 dark:text-white"
        />
      </div>
      <label className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
        <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} /> Make this my default payment method
      </label>
      <HostedCardForm
        publishableKey={publishableKey}
        stripeAccount={stripeAccount}
        mode="setup"
        begin={begin}
        submitLabel={`Save ${label}`}
        onConfirmed={async ({ paymentMethodId }) => {
          const res = await save({ paymentMethodId, isDefault, friendlyName: friendlyName || undefined });
          onSuccess?.(res?.data);
        }}
      />
      {onCancel && (
        <button type="button" onClick={onCancel} className="w-full text-sm text-neutral-500 hover:underline">
          Cancel
        </button>
      )}
    </div>
  );

  if (inline) return body;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onCancel}>
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-xl dark:bg-neutral-800" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">{title || `Add ${label}`}</h2>
          <button type="button" onClick={onCancel} className="text-neutral-400 hover:text-neutral-600" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>
        {body}
      </div>
    </div>
  );
}
